import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
} from '@mui/joy';
import { PaperPlaneTilt as SendIcon, CheckCircle as SuccessIcon } from '@phosphor-icons/react';
import { useSendTestAlert, useTestAlertOptions } from '../../hooks/useStormTracker';
import { getApiErrorMessage } from '../../lib/apiError';

interface TestAlertModalProps {
  open: boolean;
  onClose: () => void;
  territoryId: number;
  territoryName: string;
}

export const TestAlertModal: React.FC<TestAlertModalProps> = ({
  open,
  onClose,
  territoryId,
  territoryName,
}) => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sendTestAlertMutation = useSendTestAlert();
  const options = useTestAlertOptions(territoryId, open);
  const [recipientOverride, setRecipientOverride] = useState<string | null>(null);
  useEffect(() => {
    setRecipientOverride(null);
    setSuccess(false);
    setError(null);
  }, [open, territoryId]);
  const toNumber = recipientOverride ?? options.data?.to_number ?? '';

  const handleSendTestAlert = async () => {
    setError(null);
    setSuccess(false);

    try {
      await sendTestAlertMutation.mutateAsync({ territoryId, toNumber });
      setSuccess(true);
    } catch (err: unknown) {
      // The API client puts the reason in the parsed body, not in Error.message —
      // reading message alone surfaced a bare "HTTP error! status: 400".
      setError(getApiErrorMessage(err, 'Failed to send test alert'));
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ maxWidth: 400, width: '100%' }}>
        <ModalClose />
        <DialogTitle>Send Test Alert</DialogTitle>
        <DialogContent>
          {success ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <SuccessIcon size={48} color="var(--joy-palette-success-500)" style={{ marginBottom: 16 }} />
              <Typography level="body-lg" sx={{ mb: 1 }}>
                Test alert sent successfully!
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Test alert submitted for {toNumber}.
              </Typography>
            </Box>
          ) : (
            <>
              {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Typography level="body-md" sx={{ mb: 2 }}>
                Send a test SMS alert to verify your notification settings for{' '}
                <strong>{territoryName}</strong>.
              </Typography>
              <FormControl required>
                <FormLabel>Recipient phone number</FormLabel>
                <Input
                  type="tel"
                  value={toNumber}
                  disabled={sendTestAlertMutation.isPending}
                  onChange={(e) => setRecipientOverride(e.target.value)}
                />
                <FormHelperText>
                  Defaults to your alert number. Used for this test only; the number must have
                  consent for staff alerts.
                </FormHelperText>
              </FormControl>
              {options.isError && (
                <Alert color="warning">
                  Your default number could not be loaded. Enter a number above.
                </Alert>
              )}
              <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 2 }}>
                Sends one message to the number above, clearly marked as a test.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {success ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="plain" color="neutral" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                startDecorator={<SendIcon />}
                loading={sendTestAlertMutation.isPending}
                disabled={!toNumber.trim()}
                onClick={handleSendTestAlert}
              >
                Send Test Alert
              </Button>
            </>
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default TestAlertModal;
